import { ref } from 'vue'
import { DriverService } from './driver'
import { DriverSocketMsg, DriverSocketMsgType } from '@libs/types/message'
import { enableDialog, DialogType } from '@/components/cdialog/dialog'
import { loadingContent } from '@/components/loading/loading.vue'
import { KK, VK } from '@/define/define'
import { getInternalActionKeyDef, InternalActionKeyId } from '@/define/internal_action_keys'
import { Http } from '@/network/http'
import { DeviceManager, DeviceProtocol } from '../device'
import i18n from '@/languages/i18n'
import { setData } from './data'
import { actionSet } from './action_set'
import { DeviceCapability } from '@/service/device_capability'
import { magnetSet } from './magnet_set'
import { getStringAfterDash } from '@/tool/utility'
import { kbVKTexture } from '@/define/vkcode'
const { t } = i18n.global
import { compare } from 'compare-versions'
import { isElectron } from '@libs/utils/envUtils'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { triggerBasicSettingsRefresh } from './basic_settings_refresh'
import { LocalConfigRepository } from './local_config_repository'
import { OnboardConfigRepository } from './onboard_config_repository'
import type { Config, ConfigList, ConfigRepository, ConfigRepositoryResult, OnboardConfig, OnboardConfigList } from './config_repository'
import { delay } from '@libs/utils/tools'

const onboardSwitchActionIds = [InternalActionKeyId.deviceSwitchToProfile1, InternalActionKeyId.deviceSwitchToProfile2, InternalActionKeyId.deviceSwitchToProfile3, InternalActionKeyId.deviceSwitchToProfile4] as VK[]
const CONFIG_CHANGE_SAFETY_TIMEOUT_MS = 35000
export interface configModel {
  bg_img: string
  content: string
  desc: string
  id: number
  name: string
  sort: number
  sub_name: string
  valid: boolean
}

class ConfigSet {
  configList = ref<ConfigList[]>([]) // 配置列表
  currentConfig = ref<number>(0) // 当前配置
  onboardConfigList = ref<OnboardConfigList[]>([])
  currentOnboardConfig = ref<number>(-1)
  private isHandlingOnboardSwitch = false
  private isReapplyingAfterOnboardSwitch = false
  private _isShowShortcon = ref(false) // 是否显示快捷键

  /** 迁移选择器状态 */
  migrationDialogVisible = ref(false)
  migrationCandidates = ref<{ index: number; name: string; isCurrent: boolean; slotIndex: number | null }[]>([])
  private migrationResolve: (() => void) | null = null

  /** 替换槽位对话框状态 */
  replaceSlotDialogVisible = ref(false)
  replaceSlotTargetProfileIndex = ref(-1)
  get isShowShortcon() {
    return this._isShowShortcon.value
  }
  set isShowShortcon(val: boolean) {
    this._isShowShortcon.value = val
  }
  recommendConfigList = ref<configModel[]>([]) // 推荐配置列表
  changeLock = false // 切换配置锁

  /** 活跃配置列表（有 slotIndex 0-3 的配置），按槽位升序 */
  private localRepository = new LocalConfigRepository()
  private onboardRepository = new OnboardConfigRepository()

  private get repository(): ConfigRepository {
    return this.isOnboardConfigSupported ? this.onboardRepository : this.localRepository
  }

  getConfigSlotIndex(item: ConfigList): number | null {
    return item.slotIndex ?? item.OnboardSlot ?? null
  }

  get activeConfigs() {
    return this.configList.value
      .map((item, index) => ({ ...item, OnboardSlot: this.getConfigSlotIndex(item), index }))
      .filter((item) => item.OnboardSlot !== null)
      .sort((a, b) => a.OnboardSlot! - b.OnboardSlot!)
  }

  /** 存档配置列表（slotIndex === null） */
  get archivedConfigs() {
    return this.configList.value.map((item, index) => ({ ...item, OnboardSlot: this.getConfigSlotIndex(item), index })).filter((item) => item.OnboardSlot === null)
  }

  /** 当前配置的槽位索引 */
  get currentSlotIndex(): number | null {
    const current = this.configList.value[this.currentConfig.value]
    return current ? this.getConfigSlotIndex(current) : null
  }

  /**
   * 触发迁移选择器（当配置数 > 4 时）。
   * 返回一个 Promise，在用户完成选择后 resolve。
   */
  async promptMigrationIfNeeded(): Promise<void> {
    // 通过 fakesocket 获取迁移状态
    const res = await DriverService.instance.askType<{ needsMigration: boolean; candidates: { index: number; name: string; isCurrent: boolean; slotIndex: number | null }[] }>(DriverSocketMsgType.MTOnboardCfgMigrationCheck)
    if (!res.needsMigration) return

    this.migrationCandidates.value = res.candidates
    this.migrationDialogVisible.value = true

    return new Promise<void>((resolve) => {
      this.migrationResolve = resolve
    })
  }

  /**
   * 用户在迁移选择器中确认后调用。
   * @param keepIndices 用户选择保留的 4 个配置索引
   */
  async completeMigration(keepIndices: number[]) {
    const data = {
      Type: DriverSocketMsgType.MTOnboardCfgMigrationExecute,
      Data: { KeepIndices: keepIndices },
    } as DriverSocketMsg<any>

    await DriverService.instance.askTypeAndData(data)

    this.migrationDialogVisible.value = false
    this.migrationCandidates.value = []
    this.migrationResolve?.()
    this.migrationResolve = null

    // 刷新配置列表（含板载 UI）
    await this.apiConfigList()
    await this.apiOnboardConfigList()
  }

  // 是否支持绑定快捷键的功能
  get isFeatureSupported() {
    if (DeviceManager.instance.state.driverProtocol == DeviceProtocol.hid_web) {
      return false
    }
    if (isElectron() && DeviceManager.instance.state.driverServiceVersion != null && compare(DeviceManager.instance.state.driverServiceVersion, '2.1.5', '>=')) {
      return true
    }
    return false
  }

  get isOnboardConfigSupported() {
    return DeviceCapability.isOnboardConfigSupported()
  }

  /** 支持板载的设备限制 8 个配置；不支持板载的设备无上限 */
  get maxConfigCount(): number {
    return this.isOnboardConfigSupported ? 8 : Infinity
  }

  private clearOnboardConfigState() {
    this.currentOnboardConfig.value = -1
    this.onboardConfigList.value = []
  }

  private applyOnboardConfigState(onboard: OnboardConfig) {
    this.currentOnboardConfig.value = onboard.Current
    this.onboardConfigList.value = onboard.List
  }

  private applyRepositoryResult(result: ConfigRepositoryResult) {
    this.currentConfig.value = result.config.Current
    this.configList.value = result.config.List

    if (result.onboard) {
      this.applyOnboardConfigState(result.onboard)
      return
    }

    if (!this.isOnboardConfigSupported) {
      this.clearOnboardConfigState()
    }
  }

  private async refreshMagnetStateAfterConfigSwitch() {
    if (!DeviceManager.instance.state.currentDevice.IsSupportMagnet || specialCheckCurrent(speciaBehiver.DevicesOfWitmod)) {
      return
    }

    await magnetSet.getKeysInfo()

    const isSupportSingleKeyDeadZone = await magnetSet.fwIsSupportSingleKeyDeadZone()
    if (isSupportSingleKeyDeadZone) {
      await magnetSet.getKeysDeadZone()
    } else {
      await magnetSet.getDeadZone()
      if (magnetSet.deadZone.value.length >= 2) {
        const [top, bottom] = magnetSet.deadZone.value
        magnetSet.globalDeadZone.Top = top
        magnetSet.globalDeadZone.Bottom = bottom
        magnetSet.newDeadZone.Top = top
        magnetSet.newDeadZone.Bottom = bottom
      }
    }

    if (magnetSet.selectedKeys.value.length > 0) {
      magnetSet.changeViewByAddKeys([...magnetSet.selectedKeys.value])
    }
  }

  private async reapplyCurrentConfigAfterOnboardSwitch() {
    if (DeviceManager.instance.state.driverProtocol !== DeviceProtocol.hid_web) {
      return
    }
    // 晶泰 V2：固件独立管理各槽位数据，正常切槽时无需前端推送配置。
    // 但若该槽位被标记为 pendingInitialSync（如执行"同步切换键"后），
    // 需调用 change() 经由 handleCfgSwitch 执行首次整包同步，将本地数据写入硬件槽位。
    // 其他板载设备（火柴人、晶泰V1 等）维持原逻辑：固件或架构上无需前端重推。
    const isJingtaiV2 = specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2)
    if (this.isOnboardConfigSupported && !isJingtaiV2) {
      return
    }
    if (this.currentConfig.value < 0) return
    // 晶泰 V2 硬件触发切槽：固件已切换到目标槽，无需再发 setOnboardConfig 指令。
    // 仅当目标槽位处于 pendingInitialSync 状态时（如"同步切换键"操作后首次切入），
    // 才需调用 change() 将本地配置整包写入硬件。否则直接跳过，避免冗余的
    // setOnboardConfig 调用导致固件短暂重载配置、按键出现无响应或设置滞后。
    if (isJingtaiV2) {
      const currentItem = this.configList.value[this.currentConfig.value]
      const needsInitialSync = currentItem?.pendingInitialSync === true
      if (!needsInitialSync) return
    }
    const result = await this.localRepository.change(this.currentConfig.value)
    this.applyRepositoryResult(result)
  }

  handelChangeConfig() {
    DriverService.instance.setHandler(DriverSocketMsgType.MTCfgChange, async (_) => {
      // 如果是 onboard 切换后的回写触发，跳过重复刷新
      if (this.isReapplyingAfterOnboardSwitch) return
      await setData.requestSettingsRefresh()
      await this.refreshMagnetStateAfterConfigSwitch()
      actionSet.resetData()
      triggerBasicSettingsRefresh()
    })
  }

  handelSwitchConfig() {
    DriverService.instance.setHandler(
      DriverSocketMsgType.MSDeviceSwitchSnapshotStart,
      (_) => {
        this.isHandlingOnboardSwitch = true
        loadingContent.value = t('change_config_loading')
      },
      () => {
        this.isHandlingOnboardSwitch = false
        loadingContent.value = ''
        enableDialog(t('change_failed'))
      },
    )
    DriverService.instance.setHandler(
      DriverSocketMsgType.MSDeviceSwitchSnapshotEnd,
      async (res: Config) => {
        if (this.isHandlingOnboardSwitch && loadingContent.value.length === 0) {
          loadingContent.value = t('change_config_loading')
        }
        try {
          this.currentConfig.value = res.Current
          this.configList.value = res.List
          if (this.isOnboardConfigSupported) {
            await this.apiOnboardConfigList()
          } else {
            this.clearOnboardConfigState()
          }
          actionSet.resetData()
          this.isReapplyingAfterOnboardSwitch = true
          try {
            await this.reapplyCurrentConfigAfterOnboardSwitch()
          } finally {
            this.isReapplyingAfterOnboardSwitch = false
          }
          if (specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen)) {
            await delay(600) // 火柴人切换配置后固件重载时间较长，延长等待以避免设置刷新过早导致的读取失败或时序问题
          }
          await setData.requestSettingsRefresh()
          await this.refreshMagnetStateAfterConfigSwitch()
          triggerBasicSettingsRefresh()
          enableDialog(t('change_success'))
        } finally {
          this.isHandlingOnboardSwitch = false
          loadingContent.value = ''
        }
      },
      () => {
        this.isHandlingOnboardSwitch = false
        loadingContent.value = ''
        enableDialog(t('change_failed'))
      },
    )
  }

  async getConfigRecommend() {
    this.recommendConfigList.value = await Http.get<configModel[]>(
      'configRcommends',
      {
        device: DeviceManager.instance.state.currentPid,
      },
      'https://ncus.iyx.fun/',
    )
  }

  async apiConfigList() {
    if (this.isOnboardConfigSupported) {
      await this.promptMigrationIfNeeded()
    }

    const result = await this.repository.list()
    this.applyRepositoryResult(result)
  }

  async apiOnboardConfigList() {
    if (!this.isOnboardConfigSupported) {
      this.clearOnboardConfigState()
      return
    }

    const result = await this.onboardRepository.list()
    this.applyRepositoryResult(result)
  }

  async apiSyncCurrentConfigToOnboard(index: number) {
    if (!this.isOnboardConfigSupported) {
      throw new Error('onboard config not supported')
    }

    const result = await this.onboardRepository.syncCurrentToOnboard(index)
    this.applyRepositoryResult(result)
  }

  async apiRenameOnboardConfig(index: number, name: string) {
    if (!this.isOnboardConfigSupported) {
      throw new Error('onboard config not supported')
    }

    const result = await this.onboardRepository.renameOnboard(index, name)
    this.applyRepositoryResult(result)
  }

  /** 打开替换槽位对话框 */
  openReplaceSlotDialog(profileIndex: number) {
    this.replaceSlotTargetProfileIndex.value = profileIndex
    this.replaceSlotDialogVisible.value = true
  }

  /** 关闭替换槽位对话框 */
  closeReplaceSlotDialog() {
    this.replaceSlotDialogVisible.value = false
    this.replaceSlotTargetProfileIndex.value = -1
  }

  /** 替换槽位：将存档配置装入指定槽位，原槽位配置变为存档 */
  async apiReplaceSlot(profileIndex: number, targetSlot: number) {
    if (this.changeLock) return
    this.changeLock = true
    loadingContent.value = t('config_change_message')
    const safetyTimer = setTimeout(() => {
      this.updateLoadingAndLock()
      enableDialog(t('change_failed')).then(() => (this.changeLock = false))
    }, CONFIG_CHANGE_SAFETY_TIMEOUT_MS)

    try {
      const result = await this.onboardRepository.replaceSlot(profileIndex, targetSlot)
      this.applyRepositoryResult(result)
      clearTimeout(safetyTimer)
      actionSet.resetData()
      await delay(500) // 等待固件完成槽位替换的内部处理，避免紧接着的设置刷新与读取出现时序问题
      await setData.requestSettingsRefresh()
      await this.refreshMagnetStateAfterConfigSwitch()
      triggerBasicSettingsRefresh()
      this.updateLoadingAndLock()
      this.closeReplaceSlotDialog()
      enableDialog(t('change_success')).then(() => (this.changeLock = false))
    } catch {
      clearTimeout(safetyTimer)
      this.updateLoadingAndLock()
      enableDialog(t('change_failed')).then(() => (this.changeLock = false))
    }
  }

  getKeyConflictInfo(kk: KK) {
    const keyMap = setData.getData().Layout.KeyMap || {}
    const currentVK = Number(keyMap[kk]) as VK

    if (!currentVK || currentVK <= 0) {
      return null
    }

    const switchTarget = onboardSwitchActionIds.findIndex((actionId) => actionId === currentVK)
    if (switchTarget >= 0) {
      return {
        type: 'switch' as const,
        targetSlot: switchTarget,
      }
    }

    const internalAction = getInternalActionKeyDef(currentVK)
    if (internalAction) {
      return {
        type: 'action' as const,
        description: t(internalAction.labelKey),
      }
    }

    return {
      type: 'action' as const,
      description: getStringAfterDash(kbVKTexture[currentVK] || `VK ${currentVK}`),
    }
  }

  // 添加配置
  async apiAddConfig(): Promise<void> {
    if (this.configList.value.length >= this.maxConfigCount) {
      enableDialog(t('config_count_limit'), DialogType.notice, false)
      return
    }
    const result = await this.repository.create(`${t('config')}${this.configList.value.length + 1}`)
    this.applyRepositoryResult(result)
  }

  apiChangeConfig(index: number, options?: { silent?: boolean }) {
    if (this.changeLock) return Promise.resolve()
    this.changeLock = true
    loadingContent.value = t('config_change_message')
    const silent = options?.silent ?? false
    const safetyTimer = setTimeout(() => {
      console.warn('[ConfigSet] apiChangeConfig safety timeout triggered')
      this.updateLoadingAndLock()
      if (!silent) enableDialog(t('change_failed')).then(() => (this.changeLock = false))
    }, CONFIG_CHANGE_SAFETY_TIMEOUT_MS)
    return this.repository
      .change(index)
      .then((result) => {
        this.applyRepositoryResult(result)
        clearTimeout(safetyTimer)
        actionSet.resetData()
        this.updateLoadingAndLock()
        if (!silent) enableDialog(t('change_success')).then(() => (this.changeLock = false))
      })
      .catch(() => {
        clearTimeout(safetyTimer)
        this.updateLoadingAndLock()
        if (!silent) enableDialog(t('change_failed')).then(() => (this.changeLock = false))
      })
  }

  private updateLoadingAndLock() {
    loadingContent.value = ''
    // this.changeLock = false
  }

  async apiDelete(index: number): Promise<void> {
    const result = await this.repository.delete(index)
    this.applyRepositoryResult(result)
  }

  async apiReName(index: number, name: string): Promise<void> {
    const result = await this.repository.rename(index, name)
    this.applyRepositoryResult(result)
  }

  async apiBindShortcut(index: number, shortcut: VK[]): Promise<void> {
    const result = await this.repository.bindShortcut(index, this.configList.value[index].Name, shortcut)
    this.applyRepositoryResult(result)
    this.isShowShortcon = false
  }

  async getExportConfigData(index: number): Promise<string> {
    return await this.repository.export(index)
  }

  // 设置配置预设
  async setConfigPreset(name: string, contentOrUrl: any): Promise<Config> {
    let base64 = contentOrUrl
    // 如果传入的是 Vite 的资源模块对象 (src/assets)
    if (typeof contentOrUrl === 'object' && contentOrUrl.default) {
      contentOrUrl = contentOrUrl.default
    }
    if (typeof contentOrUrl === 'string' && (contentOrUrl.startsWith('/') || contentOrUrl.startsWith('http') || contentOrUrl.includes('blob:'))) {
      try {
        const response = await fetch(contentOrUrl)
        base64 = await response.text()
      } catch (e) {
        console.error('Failed to fetch preset:', e)
      }
    }
    if (typeof base64 === 'string') {
      base64 = base64.trim()
    }
    const result = await this.repository.import(name, base64)
    this.applyRepositoryResult(result)
    return result.config
  }

  // 替换指定索引的配置（配置已满时使用）
  async importReplace(name: string, base64: string, replaceIndex: number): Promise<Config> {
    const result = await this.repository.importReplace(name, base64.trim(), replaceIndex)
    this.applyRepositoryResult(result)
    return result.config
  }
}
let configSet = new ConfigSet()
export { configSet }
